import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Layout from '@shared/ui/organisms/layout/Layout';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import Colours from '@shared/ui/atoms/Colours';
import { successNotification, failureNotification } from '@shared/store/notification.duck';
import {
  getDeletedUsers,
  restoreUserRequest,
  permanentlyDeleteUserRequest,
} from '@features/settings/services/ManagementService';
import { UserInfoV2 } from '@features/settings/pages/manage-users-page-v2/ducks/ManageUsers.model';

export function DeletedUsersPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [users, setUsers] = useState<UserInfoV2[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    try {
      const result = await getDeletedUsers();
      setUsers(result ?? []);
    } catch (e) {
      console.error(e);
      dispatch(failureNotification(t('deleted-users-page.load-failed')));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUndelete = async (user: UserInfoV2) => {
    setPendingId(user.id);
    try {
      await restoreUserRequest(user.id);
      dispatch(successNotification(t('deleted-users-page.restored')));
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (e) {
      console.error(e);
      dispatch(failureNotification(t('deleted-users-page.restore-failed')));
    } finally {
      setPendingId(null);
    }
  };

  const onPermanentDelete = async (user: UserInfoV2) => {
    setPendingId(user.id);
    try {
      await permanentlyDeleteUserRequest(user.id);
      dispatch(successNotification(t('deleted-users-page.deleted-permanently')));
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (e) {
      console.error(e);
      dispatch(failureNotification(t('deleted-users-page.permanent-delete-failed')));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Layout>
      <Container>
        <GoBackButton url={'/manage/users'} text={'deleted-users-page.back-to-users'} />
        <HeadlineLarge style={{ marginBottom: 8 }}>{t('deleted-users-page.title')}</HeadlineLarge>
        <BodyRegular colour={Colours.darkGrey} style={{ marginBottom: 32 }}>{t('deleted-users-page.description')}</BodyRegular>

        {isLoading ? (
          <LoadingSpinner hideBorder={true} />
        ) : users.length === 0 ? (
          <BodyRegular>{t('deleted-users-page.empty')}</BodyRegular>
        ) : (
          <>
            <HeaderRow>
              <BodySmall weight={600} colour={Colours.darkGrey}>{t('deleted-users-page.col-name')}</BodySmall>
              <BodySmall weight={600} colour={Colours.darkGrey}>{t('deleted-users-page.col-email')}</BodySmall>
              <BodySmall weight={600} colour={Colours.darkGrey}>{t('deleted-users-page.col-team')}</BodySmall>
              <span />
            </HeaderRow>
            {users.map((user) => {
              const isPending = pendingId === user.id;
              return (
                <Row key={user.id}>
                  <NameCell>
                    <UserAvatarAndName firstName={user.firstName} lastName={user.lastName} />
                  </NameCell>
                  <Cell>
                    <BodySmall weight={400} title={user.email}>{user.email}</BodySmall>
                  </Cell>
                  <Cell>
                    {user.teamName
                      ? <BodySmall weight={400} colour={Colours.darkGrey}>{user.teamName}</BodySmall>
                      : <BodySmall weight={400} colour={Colours.mildGrey}>—</BodySmall>}
                  </Cell>
                  <Actions>
                    <OutlineButton text={'deleted-users-page.undelete'} disabled={isPending}
                                   size={ButtonSize.Small}
                                   click={() => onUndelete(user)} />
                    <OutlineButton text={'deleted-users-page.delete-permanently'} disabled={isPending}
                                   borderColour={Colours.red} size={ButtonSize.Small}
                                   click={() => onPermanentDelete(user)} />
                  </Actions>
                </Row>
              );
            })}
          </>
        )}
      </Container>
    </Layout>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 24px 64px 24px;
`;

// Shared column layout so the header and every row line up.
const COLUMNS = '220px minmax(0, 1.4fr) minmax(0, 1fr) 280px';

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  align-items: center;
  gap: 16px;
  padding: 0 0 8px 0;
  border-bottom: 1px solid ${Colours.mildGrey};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  align-items: center;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid ${Colours.lightGrey};
`;

const NameCell = styled.div`
  min-width: 0;
  overflow: hidden;
`;

const Cell = styled.div`
  min-width: 0;
  p {
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;

  /* Buttons default to width: 100% — let them size to their label so they sit
     side by side on one line instead of wrapping. */
  button {
    width: auto;
    margin: 0;
  }
`;

